package fyi.moko.hotpl.domain.service

import kotlinx.coroutines.flow.Flow

interface AuthService {
    val currentUserId: Flow<String?>
    suspend fun getCurrentUserId(): String?
    suspend fun ensureSignedIn(): String

    /**
     * Links the OAuth credential to the current (anonymous) user, preserving the uid.
     * On success the local/remote data continues seamlessly under the same identity.
     * Returns [Result.failure] wrapping [CredentialAlreadyInUseException] when the
     * credential already belongs to a different account (caller should then switch
     * via [signInWithGoogle]/[signInWithApple]).
     *
     * [accessToken] is the Google OAuth access token. It is null on Android (Credential
     * Manager only surfaces an ID token, and Firebase accepts an id-token-only Google
     * credential there) but must be non-null on iOS, where the native
     * `FIRGoogleAuthProvider` credential requires it.
     */
    suspend fun linkWithGoogle(idToken: String, accessToken: String?): Result<Unit>

    /**
     * [rawNonce] is the un-hashed random string the platform Sign in with Apple flow
     * generated before hashing it for the request; Firebase re-hashes it server-side to
     * verify the identity token wasn't replayed.
     */
    suspend fun linkWithApple(idToken: String, rawNonce: String): Result<Unit>

    /** Result of [linkOrSignInWithApple]. */
    sealed interface AppleLinkResult {
        /** Credential linked to the existing user; uid unchanged. */
        data object Linked : AppleLinkResult

        /** The credential/email already belonged to an account; signed into it instead. */
        data class SwitchedTo(val uid: String) : AppleLinkResult
    }

    /**
     * Links the Apple credential to the current user, or — when the credential/email already
     * belongs to another account, or there is no current user at all — signs into that account
     * instead.
     *
     * Apple makes this a single operation on purpose: an Apple ID token is **single-use**, so
     * the moment a link attempt fails with a collision the token is spent, and re-sending it in
     * a separate sign-in call is always rejected with `17094 "Duplicate credential received"`.
     * The recovery Firebase documents is to sign in with the *rotated* credential it attaches
     * to the collision error (`FIRAuthErrorUserInfoUpdatedCredentialKey`) — which only the
     * platform SDK's raw error exposes, hence this seam. This default implementation is the
     * naive two-step flow for platforms that don't surface Apple sign-in.
     */
    suspend fun linkOrSignInWithApple(idToken: String, rawNonce: String): Result<AppleLinkResult> {
        if (getCurrentUserId() == null) {
            return signInWithApple(idToken, rawNonce).map { AppleLinkResult.SwitchedTo(it) }
        }
        val linkResult = linkWithApple(idToken, rawNonce)
        if (linkResult.isSuccess) {
            return Result.success(AppleLinkResult.Linked)
        }
        val error = linkResult.exceptionOrNull()
        if (error !is CredentialAlreadyInUseException) {
            return Result.failure(error ?: IllegalStateException("Account link failed"))
        }
        return signInWithApple(idToken, rawNonce).map { AppleLinkResult.SwitchedTo(it) }
    }

    /**
     * Signs in with an existing OAuth credential, switching identity. Returns the new
     * uid. Used on the [CredentialAlreadyInUseException] conflict path. Default
     * implementations are unimplemented so non-Firebase [AuthService]s (test fakes,
     * DI stubs) need not override them.
     */
    suspend fun signInWithGoogle(idToken: String, accessToken: String?): Result<String> =
        Result.failure(NotImplementedError("signInWithGoogle not supported"))

    suspend fun signInWithApple(idToken: String, rawNonce: String): Result<String> =
        Result.failure(NotImplementedError("signInWithApple not supported"))

    /** True when the current session is anonymous (not yet linked to a social account). */
    suspend fun isAnonymous(): Boolean = true

    /** Email of the linked account, or null when anonymous / not available. */
    suspend fun linkedEmail(): String? = null

    /** Display name of the linked account, or null when anonymous / not set. */
    suspend fun displayName(): String? = null

    /**
     * Signs out the current user (anonymous or linked). After this call the
     * [currentUserId] flow emits `null` and the next [ensureSignedIn] mints a
     * fresh anonymous session. Default no-op so non-Firebase [AuthService]s
     * (test fakes, DI stubs) need not override it.
     */
    suspend fun signOut() = Unit
}
